import { forwardRef } from 'react';
import type { ReactNode, ComponentPropsWithoutRef } from 'react';

import { Collapse } from '@lib/ui';
import { useAccordionContext } from './Accordion.context';
import { useAccordionItemContext } from './AccordionItem.context';

type AccordionPanelProps = {
  children?: ReactNode | ((props: { open: boolean }) => ReactNode);
  className?: string | ((props: { open: boolean }) => string);
  collapseProps?: ComponentPropsWithoutRef<typeof Collapse>;
} & Omit<ComponentPropsWithoutRef<'div'>, 'children' | 'className'>;

const AccordionPanel = forwardRef<HTMLDivElement, AccordionPanelProps>(
  ({ children, className, collapseProps, ...rest }, ref) => {
    const ctx = useAccordionContext();
    const { value } = useAccordionItemContext();

    const isActive = ctx.isItemActive(value);

    return (
      <Collapse
        {...collapseProps}
        in={isActive}
        transitionDuration={ctx.transitionDuration}
        role="panel"
        id={`${ctx.panelId}-${value}`}
        aria-labelledby={`${ctx.buttonId}-${value}`}
      >
        <div
          ref={ref}
          className={typeof className === 'function' ? className({ open: isActive }) : className}
          {...rest}
        >
          {typeof children === 'function' ? children({ open: isActive }) : children}
        </div>
      </Collapse>
    );
  }
);

AccordionPanel.displayName = 'AccordionPanel';

export { AccordionPanel };
export type { AccordionPanelProps };
